import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Users, 
  Clock, 
  Coffee, 
  AlertTriangle, 
  Activity, 
  Play, 
  Pause, 
  LogOut,
  TrendingUp,
  Zap,
  User,
  Briefcase,
  MapPin
} from 'lucide-react';
import { WorkEntry, User as UserType } from '../types';
import { useRealTime } from '../contexts/RealTimeContext';
import { apiService } from '../services/api';

interface TVRealTimeDashboardProps {
  user: UserType;
  onLogout: () => void;
  onExitTVMode?: () => void;
}

interface WorkerStatus {
  id: number;
  name: string;
  isWorking: boolean;
  onBreak: boolean;
  projectName?: string;
  locationName?: string;
  startTime?: string;
  workMinutes: number;
  breakMinutes: number;
  status: 'working' | 'break' | 'offline' | 'overtime';
}

const TVRealTimeDashboard: React.FC<TVRealTimeDashboardProps> = ({ user, onLogout, onExitTVMode }) => {
  const [workers, setWorkers] = useState<WorkerStatus[]>([]);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [isLoading, setIsLoading] = useState(true);
  
  const realTime = useRealTime();

  // Update current time every second for live clock
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Load worker data
  const loadWorkerData = async () => {
    try {
      setIsLoading(true);
      const today = new Date().toISOString().split('T')[0];
      
      // Get all users and work entries
      const [allUsers, workEntriesResponse] = await Promise.all([
        apiService.getUsers(),
        apiService.getWorkEntries(today, undefined, { page: 1, pageSize: 500 })
      ]);

      const workEntries = Array.isArray(workEntriesResponse) ? 
        workEntriesResponse : 
        (workEntriesResponse.data || []);

      // Filter only workers
      const workerUsers = allUsers.filter(u => u.role === 'Worker');
      
      // Process worker statuses
      const workerStatuses: WorkerStatus[] = workerUsers.map(worker => {
        const workerEntries = workEntries.filter(entry => entry.userId === worker.id);
        const activeEntry = workerEntries.find(entry => 
          entry.isActive || (!entry.endTime && entry.startTime)
        );

        // Calculate total work minutes for today
        const totalWorkMinutes = workerEntries.reduce((total, entry) => {
          const start = new Date(entry.startTime);
          const end = entry.endTime ? new Date(entry.endTime) : new Date();
          const workTime = (end.getTime() - start.getTime()) / (1000 * 60);
          const breakTime = entry.totalBreakMinutes || 0;
          return total + Math.max(0, workTime - breakTime);
        }, 0);

        const totalBreakMinutes = workerEntries.reduce((total, entry) => 
          total + (entry.totalBreakMinutes || 0), 0);

        let status: 'working' | 'break' | 'offline' | 'overtime' = 'offline';
        
        if (activeEntry) {
          if (activeEntry.activeBreak?.isActive || 
              activeEntry.breaks?.some(b => b.isActive)) {
            status = 'break';
          } else {
            status = totalWorkMinutes > 480 ? 'overtime' : 'working'; // 8 hours = 480 minutes
          }
        }

        return {
          id: worker.id,
          name: worker.name,
          isWorking: !!activeEntry,
          onBreak: status === 'break',
          projectName: activeEntry?.projectName,
          locationName: activeEntry?.locationName,
          startTime: activeEntry?.startTime,
          workMinutes: Math.round(totalWorkMinutes),
          breakMinutes: Math.round(totalBreakMinutes),
          status
        };
      });

      setWorkers(workerStatuses);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error loading worker data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadWorkerData();
  }, []);

  // Set up real-time connection and event listeners
  useEffect(() => {
    const connectAndListen = async () => {
      try {
        console.log('TVRealTimeDashboard: Checking real-time connection state:', realTime.state.connectionState);
        // Only connect if we're in standalone mode (not embedded in another dashboard)
        const isStandalone = !window.location.pathname.includes('/admin');
        if (isStandalone && realTime.state.connectionState === 'Disconnected') {
          console.log('TVRealTimeDashboard: Attempting to connect to real-time service (standalone mode)');
          await realTime.connect(user.id.toString(), user.role);
        } else if (!isStandalone) {
          console.log('TVRealTimeDashboard: Embedded mode - using parent connection');
        }
      } catch (error) {
        console.error('TVRealTimeDashboard: Error connecting to real-time service:', error);
      }
    };

    connectAndListen();
  }, [user.id, user.role, realTime]);

  // Listen for real-time events
  useEffect(() => {
    if (!realTime.state.isConnected) return;

    console.log('Setting up real-time event listeners for TV dashboard...');

    const handleWorkEvent = () => {
      console.log('Work event received, refreshing TV dashboard data...');
      setTimeout(() => loadWorkerData(), 1000); // Small delay to ensure backend is updated
    };

    const handleBreakEvent = () => {
      console.log('Break event received, refreshing TV dashboard data...');
      setTimeout(() => loadWorkerData(), 1000);
    };

    const handleTeamEvent = () => {
      console.log('Team event received, refreshing TV dashboard data...');
      setTimeout(() => loadWorkerData(), 1000);
    };

    // Subscribe to all relevant events
    realTime.subscribeToWorkEvents(handleWorkEvent);
    realTime.subscribeToBreakEvents(handleBreakEvent);
    realTime.subscribeToTeamEvents(handleTeamEvent);

    return () => {
      // Cleanup subscriptions if needed
    };
  }, [realTime.state.isConnected, realTime, loadWorkerData]);

  // Auto-refresh every 30 seconds as fallback
  useEffect(() => {
    const autoRefresh = setInterval(() => {
      console.log('Auto-refreshing TV dashboard data...');
      loadWorkerData();
    }, 30000); // 30 seconds

    return () => clearInterval(autoRefresh);
  }, []);

  // Calculate statistics
  const stats = {
    total: workers.length,
    working: workers.filter(w => w.status === 'working').length,
    onBreak: workers.filter(w => w.status === 'break').length,
    overtime: workers.filter(w => w.status === 'overtime').length,
    offline: workers.filter(w => w.status === 'offline').length
  };

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'working': return 'bg-green-500';
      case 'break': return 'bg-yellow-500';
      case 'overtime': return 'bg-red-500';
      case 'offline': return 'bg-gray-400';
      default: return 'bg-gray-400';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'working': return 'Lucrează';
      case 'break': return 'În pauză';
      case 'overtime': return 'Ore extra';
      case 'offline': return 'Offline';
      default: return 'Necunoscut';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-6 text-white">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-6xl font-bold mb-2">Monitorizare LIVE</h1>
          <p className="text-2xl text-blue-200">Status lucrători în timp real</p>
        </div>
        <div className="text-right">
          <div className="text-4xl font-mono font-bold mb-2">
            {currentTime.toLocaleTimeString('ro-RO')}
          </div>
          <div className="text-xl text-blue-200">
            {currentTime.toLocaleDateString('ro-RO', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </div>
          <div className="flex items-center gap-2 mt-4 justify-end">
            <div className={`w-3 h-3 rounded-full ${
              realTime.state.isConnected ? 'bg-green-400 animate-pulse' : 'bg-red-400'
            }`}></div>
            <span className="text-sm">
              {realTime.state.isConnected ? 'Conectat Live' : 'Deconectat'}
            </span>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-5 gap-6 mb-8">
        <Card className="bg-white/10 border-white/20 backdrop-blur-sm">
          <CardContent className="p-6 text-center">
            <Users className="w-12 h-12 mx-auto mb-4 text-white" />
            <div className="text-4xl font-bold mb-2">{stats.total}</div>
            <div className="text-lg text-blue-200">Total Lucrători</div>
          </CardContent>
        </Card>

        <Card className="bg-green-500/20 border-green-400/30 backdrop-blur-sm">
          <CardContent className="p-6 text-center">
            <Play className="w-12 h-12 mx-auto mb-4 text-green-400" />
            <div className="text-4xl font-bold mb-2 text-green-400">{stats.working}</div>
            <div className="text-lg text-green-200">Lucrează</div>
          </CardContent>
        </Card>

        <Card className="bg-yellow-500/20 border-yellow-400/30 backdrop-blur-sm">
          <CardContent className="p-6 text-center">
            <Coffee className="w-12 h-12 mx-auto mb-4 text-yellow-400" />
            <div className="text-4xl font-bold mb-2 text-yellow-400">{stats.onBreak}</div>
            <div className="text-lg text-yellow-200">În Pauză</div>
          </CardContent>
        </Card>

        <Card className="bg-red-500/20 border-red-400/30 backdrop-blur-sm">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-400" />
            <div className="text-4xl font-bold mb-2 text-red-400">{stats.overtime}</div>
            <div className="text-lg text-red-200">Ore Extra</div>
          </CardContent>
        </Card>

        <Card className="bg-gray-500/20 border-gray-400/30 backdrop-blur-sm">
          <CardContent className="p-6 text-center">
            <Pause className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <div className="text-4xl font-bold mb-2 text-gray-400">{stats.offline}</div>
            <div className="text-lg text-gray-200">Offline</div>
          </CardContent>
        </Card>
      </div>

      {/* Workers Grid */}
      <div className="grid grid-cols-4 gap-6">
        {workers.map((worker) => (
          <Card 
            key={worker.id} 
            className={`bg-white/10 border-white/20 backdrop-blur-sm transition-all duration-300 ${
              worker.status === 'working' ? 'ring-2 ring-green-400 bg-green-500/10' :
              worker.status === 'break' ? 'ring-2 ring-yellow-400 bg-yellow-500/10' :
              worker.status === 'overtime' ? 'ring-2 ring-red-400 bg-red-500/10' :
              'opacity-60'
            }`}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded-full ${getStatusColor(worker.status)} ${
                    worker.isWorking ? 'animate-pulse' : ''
                  }`}></div>
                  <div className="text-lg font-semibold truncate">{worker.name}</div>
                </div>
                <Badge 
                  className={`text-sm px-3 py-1 ${
                    worker.status === 'working' ? 'bg-green-500/20 text-green-400 border-green-400/30' :
                    worker.status === 'break' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-400/30' :
                    worker.status === 'overtime' ? 'bg-red-500/20 text-red-400 border-red-400/30' :
                    'bg-gray-500/20 text-gray-400 border-gray-400/30'
                  }`}
                >
                  {getStatusText(worker.status)}
                </Badge>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              {worker.isWorking && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Briefcase className="w-4 h-4 text-blue-400" />
                    <span className="truncate">{worker.projectName || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-blue-400" />
                    <span className="truncate">{worker.locationName || 'N/A'}</span>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span>Ore lucrate:</span>
                  <span className={`font-semibold ${
                    worker.workMinutes > 480 ? 'text-red-400' : 'text-green-400'
                  }`}>
                    {formatTime(worker.workMinutes)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center text-sm">
                  <span>Pauze:</span>
                  <span className={`font-semibold ${
                    worker.breakMinutes > 60 ? 'text-yellow-400' : 'text-blue-200'
                  }`}>
                    {formatTime(worker.breakMinutes)}
                  </span>
                </div>

                {worker.isWorking && worker.startTime && (
                  <div className="space-y-2">
                    <div className="text-xs text-blue-200">
                      Început: {new Date(worker.startTime).toLocaleTimeString('ro-RO')}
                    </div>
                    <Progress 
                      value={Math.min((worker.workMinutes / 480) * 100, 100)} 
                      className="h-2 bg-white/20"
                    />
                    <div className="text-xs text-center text-blue-200">
                      {((worker.workMinutes / 480) * 100).toFixed(1)}% din 8h
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Footer with last update info */}
      <div className="fixed bottom-4 left-6 right-6 flex justify-between items-center text-sm text-blue-200">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4" />
          <span>Ultima actualizare: {lastUpdate.toLocaleTimeString('ro-RO')}</span>
        </div>
        <div className="flex items-center gap-4">
          <span>Administrator: {user.name}</span>
          {onExitTVMode && (
            <button
              onClick={onExitTVMode}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 border border-blue-400/30 rounded-lg hover:bg-blue-500/30 transition-colors"
            >
              <Activity className="w-4 h-4" />
              Mod Normal
            </button>
          )}
          <button
            onClick={onLogout}
            className="flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-400/30 rounded-lg hover:bg-red-500/30 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Ieșire
          </button>
        </div>
      </div>
    </div>
  );
};

export default TVRealTimeDashboard;